/* ==========================================================================
   AG theme - global behaviour (vanilla JS, no dependencies)
   - <ag-announcement>   cross-fading message rotator
   - <ag-header>         transparent-over-hero state, mobile drawer, search drawer, mega menu keys
   - footer accordions, wordmark auto-fit, scroll-to-top
   - AGWishlist          localStorage wishlist + bookmark buttons
   ========================================================================== */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopQuery = window.matchMedia('(min-width: 1100px)');

  /* ---------------- Announcement bar ---------------- */
  class AgAnnouncement extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.ag-announcement__slide'));
      this.index = 0;
      const speed = parseInt(this.dataset.speed, 10) || 1000;
      const delay = parseInt(this.dataset.delay, 10) || 2500;
      this.style.setProperty('--ag-ann-speed', `${speed}ms`);
      if (this.slides.length < 2 || this.dataset.autoplay !== 'true' || reduceMotion.matches) return;
      this.timer = window.setInterval(() => this.next(), delay + speed);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    next() {
      this.slides[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.slides.length;
      this.slides[this.index].classList.add('is-active');
    }
  }
  if (!customElements.get('ag-announcement')) customElements.define('ag-announcement', AgAnnouncement);

  /* ---------------- Header ---------------- */
  class AgHeader extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.ag-header__bar');
      this.mobileNav = this.querySelector('#AgMobileNav');
      this.search = this.querySelector('#AgSearch');
      this.account = this.querySelector('#AgAccount');
      this.accountMenu = this.querySelector('[data-ag-account-menu]');
      this.wishlist = this.querySelector('#AgWishlist');
      this.burger = this.querySelector('[data-ag-toggle="mobile-nav"]');
      this.overlayEnabled = this.dataset.overlay === 'true';
      this.openLayers = new Set();

      this.updateHeights();
      window.addEventListener('resize', () => this.updateHeights(), { passive: true });

      this.addEventListener('click', (event) => this.onClick(event));
      document.addEventListener('click', (event) => {
        if (!event.target.closest('.ag-account-wrap')) this.closeAccountMenu();
      });
      this.addEventListener('submit', (event) => this.onSubmit(event));
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        this.closeAccountMenu();
        if (this.wishlist && this.openLayers.has(this.wishlist)) this.closeWishlist();
        else if (this.account && this.openLayers.has(this.account)) this.closeAccount();
        else if (this.openLayers.has(this.search)) this.closeSearch();
        else if (this.openLayers.has(this.mobileNav)) this.closeMobileNav();
        this.querySelectorAll('.ag-nav__item.is-open').forEach((item) => item.classList.remove('is-open'));
        if (this.contains(document.activeElement) && document.activeElement.closest('.ag-mega')) {
          document.activeElement.closest('.ag-nav__item')?.querySelector('.ag-nav__link')?.focus();
        }
      });

      desktopQuery.addEventListener('change', () => {
        if (desktopQuery.matches && this.openLayers.has(this.mobileNav)) this.closeMobileNav();
      });

      this.setupTransparency();
      document.addEventListener('shopify:section:load', () => this.applyScrollState());
    }

    updateHeights() {
      const ann = document.querySelector('.ag-announcement');
      const root = document.documentElement.style;
      root.setProperty('--ag-header-height', `${this.bar.offsetHeight}px`);
      if (ann) root.setProperty('--ag-announcement-height', `${ann.offsetHeight}px`);
    }

    /* Header look follows whatever section sits under it:
       - full-bleed hero (data-ag-hero + data-ag-tone): transparent, light or dark text
       - dark section (data-ag-tone="light", no hero): solid dark bar
       - anything else: solid scheme colours */
    setupTransparency() {
      if (!this.scrollBound) {
        this.scrollBound = true;
        const onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          window.requestAnimationFrame(() => {
            this.ticking = false;
            this.applyScrollState();
          });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
      }
      this.applyScrollState();
    }

    applyScrollState() {
      if (document.body.classList.contains('ag-slider-active')) return; // slider controls the state
      let transparent = false;
      let dark = false;
      let tone = 'light';
      if (this.overlayEnabled && !this.openLayers.size) {
        const rect = this.bar.getBoundingClientRect();
        const y = Math.min(window.innerHeight - 1, Math.max(1, rect.top + rect.height / 2));
        const stack = document.elementsFromPoint(window.innerWidth / 2, y);
        for (const el of stack) {
          if (this.contains(el)) continue;
          const zone = el.closest('[data-ag-tone]');
          if (zone) {
            tone = zone.dataset.agTone === 'dark' ? 'dark' : 'light';
            if (zone.hasAttribute('data-ag-hero')) transparent = true;
            else if (tone === 'light') dark = true;
            break;
          }
          if (el === document.body) break;
        }
      }
      this.classList.toggle('is-transparent', transparent);
      this.classList.toggle('is-dark', dark);
      this.dataset.tone = tone;
    }

    onClick(event) {
      const toggle = event.target.closest('[data-ag-toggle]');
      if (toggle) {
        const target = toggle.dataset.agToggle;
        if (target === 'mobile-nav') {
          this.openLayers.has(this.mobileNav) ? this.closeMobileNav() : this.openMobileNav();
        } else if (target === 'search') {
          this.openSearch();
        } else if (target === 'wishlist' && this.wishlist) {
          event.preventDefault();
          this.openWishlist();
        } else if (target === 'account' && this.account) {
          event.preventDefault();
          if (this.openLayers.has(this.mobileNav)) this.closeMobileNav();
          if (this.isSignedIn()) {
            if (this.accountMenu && this.accountMenu.offsetParent !== null) this.toggleAccountMenu();
            else window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'pages/account';
          }
          /* KwikPass (phone OTP login app) owns sign-in when its embed is on; our own popup is the fallback */
          else if (typeof window.handleKpAndShopifyLogin === 'function') window.handleKpAndShopifyLogin();
          else this.openAccount();
        }
        return;
      }
      if (event.target.closest('[data-ag-close="wishlist"]')) {
        this.closeWishlist();
        return;
      }
      if (event.target.closest('[data-ag-close="account"]')) {
        this.closeAccount();
        return;
      }
      if (event.target.closest('[data-ag-close="search"]')) {
        this.closeSearch();
        return;
      }
      const modeSwitch = event.target.closest('[data-ag-account-switch]');
      if (modeSwitch) {
        this.switchAccountMode(modeSwitch);
        return;
      }
      const panelOpen = event.target.closest('[data-ag-panel-open]');
      if (panelOpen) {
        this.showPanel(panelOpen.dataset.agPanelOpen, panelOpen);
        return;
      }
      if (event.target.closest('[data-ag-panel-back]')) {
        this.showPanel('root');
      }
    }

    markState() {
      this.applyScrollState();
      if (document.body.classList.contains('ag-slider-active') && this.openLayers.size) {
        this.classList.remove('is-transparent');
      } else if (document.body.classList.contains('ag-slider-active')) {
        this.classList.add('is-transparent');
      }
    }

    openMobileNav() {
      const rect = this.bar.getBoundingClientRect();
      document.documentElement.style.setProperty('--ag-mnav-top', `${Math.round(rect.bottom)}px`);
      this.mobileNav.classList.add('is-open');
      this.openLayers.add(this.mobileNav);
      this.burger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('ag-lock');
      this.markState();
    }

    closeMobileNav() {
      this.mobileNav.classList.remove('is-open');
      this.openLayers.delete(this.mobileNav);
      this.burger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('ag-lock');
      window.setTimeout(() => this.showPanel('root'), 300);
      this.markState();
    }

    showPanel(name, trigger) {
      this.mobileNav.querySelectorAll('.ag-mnav__panel').forEach((panel) => {
        const active = panel.dataset.panel === name;
        panel.classList.toggle('is-active', active);
        if (!panel.classList.contains('is-root')) panel.hidden = false;
      });
      this.mobileNav.querySelectorAll('[data-ag-panel-open]').forEach((btn) => {
        btn.setAttribute('aria-expanded', btn.dataset.agPanelOpen === name ? 'true' : 'false');
      });
      if (trigger) {
        this.mobileNav.querySelector(`[data-panel="${name}"] .ag-mnav__back`)?.focus({ preventScroll: true });
      }
    }

    openSearch() {
      this.search.classList.add('is-open');
      this.openLayers.add(this.search);
      document.body.classList.add('ag-lock');
      this.lastFocus = document.activeElement;
      const input = this.search.querySelector('.ag-search__input');
      window.setTimeout(() => input && input.focus({ preventScroll: true }), 80);
      this.markState();
    }

    openWishlist() {
      this.wishlist.render();
      this.wishlist.classList.add('is-open');
      this.openLayers.add(this.wishlist);
      document.body.classList.add('ag-lock');
      this.lastFocus = document.activeElement;
      window.setTimeout(() => this.wishlist.querySelector('.ag-wl__close')?.focus({ preventScroll: true }), 80);
      this.markState();
    }

    closeWishlist() {
      this.wishlist.classList.remove('is-open');
      this.openLayers.delete(this.wishlist);
      if (!this.openLayers.size) document.body.classList.remove('ag-lock');
      this.lastFocus?.focus?.({ preventScroll: true });
      this.markState();
    }

    /* Save explicit consent in Shopify before handing over to its login:
       - email mode + marketing box: create/update a customer subscribed to marketing (storefront customer form)
       - mobile mode + WhatsApp box: save number + consent as cart attributes (shown on the order) */
    onSubmit(event) {
      const form = event.target.closest('[data-ag-account-form]');
      if (!form || form.dataset.submitting === 'true') return;
      event.preventDefault();
      form.dataset.submitting = 'true';
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const mode = form.dataset.mode;
      const marketing = form.querySelector('[data-ag-marketing]');
      const whatsapp = form.querySelector('[data-ag-whatsapp]');
      const email = form.querySelector('[data-ag-account-input="email"]');
      const phone = form.querySelector('[data-ag-account-input="phone"]');
      const prefix = form.querySelector('.ag-account__prefix');
      const jobs = [];

      if (mode === 'email' && marketing && marketing.checked && email && email.value) {
        const body = new URLSearchParams({
          form_type: 'customer',
          utf8: '\u2713',
          'contact[email]': email.value.trim(),
          'contact[tags]': 'account-signup',
        });
        jobs.push(fetch(root + 'contact#ContactFooter', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body }).catch(() => null));
      }
      if (mode === 'phone' && whatsapp && whatsapp.checked && phone && phone.value) {
        const number = ((prefix && prefix.textContent.trim()) || '') + phone.value.replace(/\D/g, '');
        jobs.push(
          fetch(root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { whatsapp_consent: 'Yes', whatsapp_number: number } }),
          }).catch(() => null)
        );
      }
      try {
        window.localStorage.setItem('ag:consent', JSON.stringify({ mode, marketing: !!(marketing && marketing.checked), whatsapp: !!(whatsapp && whatsapp.checked), at: Date.now() }));
      } catch (err) {
        /* ignore */
      }
      Promise.all(jobs).then(() => form.submit());
    }

    /* Signed in = Shopify knows the customer, or KwikPass holds a login token in this browser */
    isSignedIn() {
      if (this.dataset.agCustomer === 'true') return true;
      try {
        const env = window.merchantInfo && window.merchantInfo.environment;
        const name = typeof window.XGokwikCoreToken === 'function' ? window.XGokwikCoreToken(env) : null;
        if (!name) return false;
        return !!(window.kpGetCookieValue && window.kpGetCookieValue(name)) || !!(window.kpGetLocalStorageValue && window.kpGetLocalStorageValue(name));
      } catch (err) {
        return false;
      }
    }

    toggleAccountMenu() {
      const open = this.accountMenu.hidden;
      this.accountMenu.hidden = !open;
      this.querySelector('[data-ag-toggle="account"]')?.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    closeAccountMenu() {
      if (!this.accountMenu || this.accountMenu.hidden) return;
      this.accountMenu.hidden = true;
      this.querySelector('[data-ag-toggle="account"]')?.setAttribute('aria-expanded', 'false');
    }

    switchAccountMode(button) {
      const form = button.closest('[data-ag-account-form]');
      const next = form.dataset.mode === 'phone' ? 'email' : 'phone';
      form.dataset.mode = next;
      ['phone', 'email'].forEach((mode) => {
        const field = form.querySelector('.ag-account__field--' + mode);
        const input = form.querySelector('[data-ag-account-input="' + mode + '"]');
        const active = mode === next;
        field.hidden = !active;
        input.disabled = !active;
        input.required = active;
      });
      form.querySelectorAll('[data-ag-for]').forEach((el) => {
        el.hidden = el.dataset.agFor !== next;
      });
      button.textContent = next === 'phone' ? button.dataset.labelEmail : button.dataset.labelPhone;
      form.querySelector('[data-ag-account-input="' + next + '"]').focus({ preventScroll: true });
    }

    openAccount() {
      this.account.classList.add('is-open');
      this.openLayers.add(this.account);
      document.body.classList.add('ag-lock');
      this.lastFocus = document.activeElement;
      const input = this.account.querySelector('.ag-account__input');
      window.setTimeout(() => input && input.focus({ preventScroll: true }), 80);
      this.markState();
    }

    closeAccount() {
      this.account.classList.remove('is-open');
      this.openLayers.delete(this.account);
      if (!this.openLayers.size) document.body.classList.remove('ag-lock');
      this.lastFocus?.focus?.({ preventScroll: true });
      this.markState();
    }

    closeSearch() {
      this.search.classList.remove('is-open');
      this.openLayers.delete(this.search);
      if (!this.openLayers.has(this.mobileNav)) document.body.classList.remove('ag-lock');
      this.lastFocus?.focus?.({ preventScroll: true });
      this.markState();
    }
  }
  if (!customElements.get('ag-header')) customElements.define('ag-header', AgHeader);

  /* ---------------- Footer accordions (mobile) ---------------- */
  const syncFooterAccordions = () => {
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    document.querySelectorAll('.ag-footer__col').forEach((details) => {
      if (details.dataset.agInit !== 'true') {
        details.dataset.agInit = 'true';
        details.querySelector('summary')?.addEventListener('click', (event) => {
          if (!window.matchMedia('(max-width: 749px)').matches) event.preventDefault();
        });
        details.open = !mobile;
      } else if (!mobile) {
        details.open = true;
      }
    });
  };

  /* ---------------- Fit wordmark to container width ---------------- */
  const fitText = (el) => {
    const parent = el.parentElement;
    if (!parent) return;
    el.style.fontSize = '100px';
    el.style.display = 'inline-block';
    const natural = el.getBoundingClientRect().width;
    el.style.display = '';
    if (!natural) return;
    const size = Math.floor((100 * parent.clientWidth) / natural);
    el.style.fontSize = `${size}px`;
    el.style.lineHeight = '0.95';
  };

  const fitAll = () => document.querySelectorAll('[data-ag-fit]').forEach(fitText);

  /* Login / create-account links: Shopify's sign-in page, its register page, or the account domain root */
  const authLink = (link) => {
    if (!link || link.closest('.ag-acct')) return null;
    try {
      const url = new URL(link.href, window.location.href);
      const onAccountHost = url.hostname.indexOf('account.') === 0 && (url.pathname === '/' || url.pathname === '');
      return /^\/account\/(login|register)\/?$/.test(url.pathname) || onAccountHost ? link : null;
    } catch (err) {
      return null;
    }
  };

  /* ---------------- Sign in / sign out buttons (KwikPass first, Shopify as fallback) ---------------- */
  document.addEventListener('click', (event) => {
    const logout = event.target.closest('[data-ag-logout]');
    if (logout) {
      event.preventDefault();
      if (typeof window.handleLogout === 'function') window.handleLogout();
      else window.location.href = logout.dataset.logoutUrl || '/account/logout';
      return;
    }
    if (event.defaultPrevented) return;
    const login = event.target.closest('[data-ag-login]') || authLink(event.target.closest('a[href]'));
    if (login && typeof window.handleKpAndShopifyLogin === 'function') {
      event.preventDefault();
      const header = document.querySelector('ag-header');
      if (header) {
        if (header.wishlist && header.openLayers.has(header.wishlist)) header.closeWishlist();
        if (header.mobileNav && header.openLayers.has(header.mobileNav)) header.closeMobileNav();
      }
      window.handleKpAndShopifyLogin();
    } else if (login && login.hasAttribute('data-ag-login')) {
      event.preventDefault();
      window.location.href = login.getAttribute('href') || '/account/login';
    }
  });

  /* ---------------- Scroll to top ---------------- */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-ag-scroll-top]');
    if (!btn) return;
    if (window.agHomeSlider) {
      window.agHomeSlider.slideTo(0, 800);
    } else {
      window.scrollTo({ top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
  });

  /* ---------------- Wishlist (localStorage) ---------------- */
  const STORAGE_KEY = 'ag:wishlist';
  const AGWishlist = {
    read() {
      try {
        return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || [];
      } catch (e) {
        return [];
      }
    },
    write(list) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      } catch (e) {
        /* storage unavailable: keep in memory only */
      }
      document.dispatchEvent(new CustomEvent('ag:wishlist:change', { detail: list }));
    },
    has(handle) {
      return this.read().includes(handle);
    },
    toggle(handle) {
      const list = this.read();
      const idx = list.indexOf(handle);
      if (idx > -1) list.splice(idx, 1);
      else list.push(handle);
      this.write(list);
      return idx === -1;
    },
    refreshUI() {
      const list = this.read();
      document.querySelectorAll('[data-ag-wishlist-count]').forEach((el) => {
        el.textContent = list.length;
      });
      document.querySelectorAll('[data-ag-wishlist-toggle]').forEach((btn) => {
        const on = list.includes(btn.dataset.handle);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.classList.toggle('is-active', on);
      });
    },
  };
  window.AGWishlist = AGWishlist;

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-ag-wishlist-toggle]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    AGWishlist.toggle(btn.dataset.handle);
  });
  document.addEventListener('ag:wishlist:change', () => AGWishlist.refreshUI());
  window.addEventListener('storage', () => AGWishlist.refreshUI());

  /* Wishlist page: renders saved products from the storefront product JSON */
  class AgWishlistGrid extends HTMLElement {
    connectedCallback() {
      this.render();
      document.addEventListener('ag:wishlist:change', () => this.render());
    }

    formatMoney(cents) {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(
          cents / 100
        );
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    }

    async render() {
      const handles = AGWishlist.read();
      const empty = this.querySelector('[data-ag-wishlist-empty]');
      const grid = this.querySelector('[data-ag-wishlist-items]');
      empty.hidden = handles.length > 0;
      grid.innerHTML = '';
      const products = await Promise.all(
        handles.map((handle) =>
          fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`)
            .then((res) => (res.ok ? res.json() : null))
            .catch(() => null)
        )
      );
      products.filter(Boolean).forEach((product) => {
        const li = document.createElement('li');
        li.className = 'ag-card';
        const image = product.featured_image ? `${product.featured_image.split('?')[0]}?width=800` : '';
        li.innerHTML = `
          <div class="ag-card__media"><a href="${product.url}">${
            image ? `<img src="${image}" alt="" loading="lazy" width="800" height="1120">` : ''
          }</a>
          <button type="button" class="ag-card__wish is-active" data-ag-wishlist-toggle data-handle="${product.handle}" aria-pressed="true" aria-label="Remove from wishlist"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><path d="M6.5 3.5h11v17l-5.5-4.2-5.5 4.2v-17Z"/></svg></button></div>
          <div class="ag-card__info"><a class="ag-card__title" href="${product.url}"></a><span class="ag-card__price">${this.formatMoney(product.price)}</span></div>`;
        li.querySelector('.ag-card__title').textContent = product.title;
        grid.appendChild(li);
      });
    }
  }
  if (!customElements.get('ag-wishlist-grid')) customElements.define('ag-wishlist-grid', AgWishlistGrid);

  /* Wishlist drawer: renders saved products as rows */
  class AgWishlistDrawer extends HTMLElement {
    connectedCallback() {
      document.addEventListener('ag:wishlist:change', () => this.classList.contains('is-open') && this.render());
    }

    formatMoney(cents) {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    }

    async render() {
      const handles = AGWishlist.read();
      const list = this.querySelector('[data-ag-wl-items]');
      const empty = this.querySelector('[data-ag-wl-empty]');
      empty.hidden = handles.length > 0;
      list.innerHTML = '';
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const products = await Promise.all(
        handles.map((h) => fetch(root + 'products/' + h + '.js').then((r) => (r.ok ? r.json() : null)).catch(() => null))
      );
      products.filter(Boolean).forEach((p) => {
        const li = document.createElement('li');
        li.className = 'ag-wl__item';
        const img = p.featured_image ? p.featured_image.split('?')[0] + '?width=240' : '';
        li.innerHTML =
          '<a class="ag-wl__thumb" href="' + p.url + '">' + (img ? '<img src="' + img + '" alt="" width="80" height="112" loading="lazy">' : '') + '</a>' +
          '<div class="ag-wl__info"><a class="ag-wl__name" href="' + p.url + '"></a><span class="ag-wl__price">' + this.formatMoney(p.price) + '</span>' +
          '<a class="ag-wl__view ag-link-underline" href="' + p.url + '"></a></div>' +
          '<button type="button" class="ag-wl__remove" data-ag-wishlist-toggle data-handle="' + p.handle + '" aria-pressed="true"></button>';
        li.querySelector('.ag-wl__name').textContent = p.title;
        li.querySelector('.ag-wl__view').textContent = this.dataset.viewLabel || 'View product';
        li.querySelector('.ag-wl__remove').setAttribute('aria-label', this.dataset.removeLabel || 'Remove from wishlist');
        li.querySelector('.ag-wl__remove').innerHTML =
          '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><path d="M6.5 3.5h11v17l-5.5-4.2-5.5 4.2v-17Z"/></svg>';
        list.appendChild(li);
      });
    }
  }
  if (!customElements.get('ag-wishlist-drawer')) customElements.define('ag-wishlist-drawer', AgWishlistDrawer);

  /* ---------------- Boot ---------------- */
  const boot = () => {
    syncFooterAccordions();
    AGWishlist.refreshUI();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitAll);
    fitAll();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  window.addEventListener('resize', () => {
    syncFooterAccordions();
    fitAll();
  });
  document.addEventListener('shopify:section:load', boot);
})();
